(function () {
  if (window.customElements && customElements.get('sale-entry-popup')) return;

  var DAY = 86400000;

  /**
   * "2D 5H 57M 04S" — matches the wording used on the announcement bar.
   * Minutes and seconds are padded so the line doesn't jitter on every tick.
   */
  function compactPhrase(target) {
    var diff = Math.max(0, target - Date.now());
    var s = Math.floor(diff / 1000);
    var dd = Math.floor(s / 86400);
    var hh = Math.floor((s % 86400) / 3600);
    var mm = Math.floor((s % 3600) / 60);
    var ss = s % 60;
    var p = function (n) { return String(n).padStart(2, '0'); };
    return (dd > 0 ? dd + 'D ' : '') + hh + 'H ' + p(mm) + 'M ' + p(ss) + 'S';
  }

  class SaleEntryPopup extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      var d = this.dataset;
      this.overlay = this.querySelector('[data-sep-overlay]');
      this.panel = this.querySelector('[data-sep-panel]');
      if (!this.overlay) return;

      this.key = 'sep-' + (d.sectionId || 'entry');
      this.frequency = d.frequency || 'session';
      this.freqDays = parseInt(d.frequencyDays, 10) || 1;
      this.maxViews = parseInt(d.maxViews, 10) || 1;
      this.delay = Math.max(0, parseInt(d.delay, 10) || 0);
      this.trigger = d.trigger || 'delay';
      this.scrollPercent = parseInt(d.scrollPercent, 10) || 30;

      var self = this;

      this.clock = this.querySelector('[data-sep-clock]');
      this.clockValue = this.querySelector('[data-sep-clock-value]');
      this.saleEnd = new Date(d.saleEnd).getTime();
      if (isNaN(this.saleEnd)) this.saleEnd = 0;
      if (this.clock) {
        this.tickClock();
        this.clockTimer = setInterval(function () { self.tickClock(); }, 1000);
      }

      this.querySelectorAll('[data-sep-close]').forEach(function (b) {
        b.addEventListener('click', function (e) { e.preventDefault(); self.close(true); });
      });
      this.querySelectorAll('[data-sep-cta]').forEach(function (b) {
        b.addEventListener('click', function () { self.record(); });
      });

      this._onKey = function (e) { if (e.key === 'Escape') self.close(true); };

      // The theme editor should always render it, so merchants can style it live.
      if (window.Shopify && window.Shopify.designMode) {
        this.open();
        document.addEventListener('shopify:section:select', function (evt) {
          if (evt.detail && evt.detail.sectionId && self.id.indexOf(evt.detail.sectionId) !== -1) self.open();
        });
        return;
      }

      if (!this.allowedOnThisPage()) return;
      if (!this.withinFrequencyCap()) return;

      this.arm();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
      if (this._timer) clearTimeout(this._timer);
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onExit) document.removeEventListener('mouseout', this._onExit);
      if (this.clockTimer) clearInterval(this.clockTimer);
    }

    /* ------------------------------ triggers ------------------------------ */

    arm() {
      var self = this;

      if (this.trigger === 'scroll') {
        this._onScroll = function () {
          var h = document.documentElement.scrollHeight - window.innerHeight;
          var pct = h > 0 ? (window.scrollY / h) * 100 : 100;
          if (pct >= self.scrollPercent) { self.fire(); }
        };
        window.addEventListener('scroll', this._onScroll, { passive: true });
        return;
      }

      if (this.trigger === 'exit') {
        this._onExit = function (e) {
          if (!e.relatedTarget && e.clientY <= 0) self.fire();
        };
        document.addEventListener('mouseout', this._onExit);
        // Touch devices have no exit intent — fall back to a generous delay.
        this._timer = setTimeout(function () { self.fire(); }, Math.max(this.delay, 15) * 1000);
        return;
      }

      this._timer = setTimeout(function () { self.fire(); }, this.delay * 1000);
    }

    fire() {
      if (this._fired) return;
      this._fired = true;
      if (this._timer) clearTimeout(this._timer);
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onExit) document.removeEventListener('mouseout', this._onExit);
      if (!this.withinFrequencyCap()) return;
      this.open();
      this.record();
    }

    /* --------------------------- page targeting --------------------------- */

    allowedOnThisPage() {
      var mode = this.dataset.showOn || 'all';
      if (this.dataset.skipCart === 'true' && /^\/(cart|checkout)/.test(location.pathname)) return false;
      if (mode === 'all') return true;

      var rules = (this.dataset.pageRules || '')
        .split('\n').map(function (r) { return r.trim().toLowerCase(); }).filter(Boolean);
      if (!rules.length) return mode === 'exclude';

      var matched = rules.some(this.matchesRule.bind(this));
      return mode === 'include' ? matched : !matched;
    }

    matchesRule(rule) {
      var path = location.pathname.toLowerCase().replace(/\/$/, '') || '/';
      var template = (this.dataset.template || '').toLowerCase();
      var handle = (this.dataset.handle || '').toLowerCase();

      if (rule === template || rule === template.split('.')[0]) return true;
      if (rule === handle) return true;

      var r = rule.replace(/\/$/, '') || '/';
      if (r.indexOf('*') !== -1) {
        var re = new RegExp('^' + r.split('*').map(function (s) {
          return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }).join('.*') + '$');
        return re.test(path);
      }
      return r === path;
    }

    /* ---------------------------- frequency ------------------------------- */

    read() {
      try { return JSON.parse(window.localStorage.getItem(this.key) || '{}') || {}; }
      catch (e) { return {}; }
    }

    write(state) {
      try { window.localStorage.setItem(this.key, JSON.stringify(state)); } catch (e) {}
    }

    seenThisSession() {
      try { return window.sessionStorage.getItem(this.key) === '1'; } catch (e) { return false; }
    }

    withinFrequencyCap() {
      var state = this.read();
      var views = state.views || 0;
      var last = state.last || 0;

      switch (this.frequency) {
        case 'always':  return true;
        case 'session': return !this.seenThisSession();
        case 'once':    return views < 1;
        case 'days':    return !last || (Date.now() - last) >= this.freqDays * DAY;
        case 'count':   return views < this.maxViews;
        default:        return !this.seenThisSession();
      }
    }

    record() {
      if (this._recorded) return;
      this._recorded = true;
      var state = this.read();
      this.write({ views: (state.views || 0) + 1, last: Date.now() });
      try { window.sessionStorage.setItem(this.key, '1'); } catch (e) {}
    }

    /* ------------------------------- popup -------------------------------- */

    /** Only shown while there is time left, so it never reads "0H 0M". */
    tickClock() {
      var running = this.saleEnd > 0 && Date.now() < this.saleEnd;
      if (running && this.clockValue) this.clockValue.textContent = compactPhrase(this.saleEnd);
      this.clock.hidden = !running;
    }

    open() {
      this.overlay.hidden = false;
      document.addEventListener('keydown', this._onKey);
      document.documentElement.style.overflow = 'hidden';
      var close = this.querySelector('[data-sep-close]');
      if (close) setTimeout(function () { close.focus({ preventScroll: true }); }, 60);
      this.dispatchEvent(new CustomEvent('sep:open', { bubbles: true }));
    }

    close(dismissed) {
      this.overlay.hidden = true;
      document.removeEventListener('keydown', this._onKey);
      document.documentElement.style.overflow = '';
      if (dismissed) this.record();
      this.dispatchEvent(new CustomEvent('sep:close', { bubbles: true }));
    }
  }

  customElements.define('sale-entry-popup', SaleEntryPopup);
})();
